import React, { useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import { Text } from "@react-three/drei";

// In-world VR comfort layer: spatial gold subtitle, head-anchored vitality/timer
// panel. Renders ONLY inside an XR session. Locomotion lives in VRRig
// (SceneCanvas.jsx), which owns the single XROrigin - a second XROrigin here
// would capture the XR camera and leave the thumbsticks moving an empty group.
export default function VRComfort({ game }) {
  const session = useXR((s) => s.session);
  const camera = useThree((s) => s.camera);
  const subRef = useRef();
  const hudRef = useRef();
  const [text, setText] = useState("");
  const [hud, setHud] = useState({ countdown: "56:00", breath: 100, stamina: 100 });

  const dir = useMemo(() => new THREE.Vector3(), []);
  const fwd = useMemo(() => new THREE.Vector3(), []);
  const right = useMemo(() => new THREE.Vector3(), []);
  const headPos = useMemo(() => new THREE.Vector3(), []);
  const headQuat = useMemo(() => new THREE.Quaternion(), []);

  useFrame(() => {
    if (!session) return;
    const h = game.current.hud || {};
    const line = h.line ? `“${h.line.text}”` : "";
    if (line !== text) setText(line);
    if (h.countdown !== hud.countdown || Math.round(h.breath) !== hud.breath) {
      setHud({ countdown: h.countdown, breath: Math.round(h.breath), stamina: Math.round(h.stamina) });
    }
    // head pose in world space, so the panels follow the player as they move
    camera.getWorldPosition(headPos);
    camera.getWorldQuaternion(headQuat);
    // spatial subtitle ~1.9m in front, billboarded
    fwd.set(0, 0, -1).applyQuaternion(headQuat);
    if (subRef.current) {
      subRef.current.position.copy(headPos).add(dir.copy(fwd).multiplyScalar(1.9));
      subRef.current.position.y = headPos.y - 0.35;
      subRef.current.quaternion.copy(headQuat);
    }
    // comfort HUD lower-left (wrist-approx)
    right.set(1, 0, 0).applyQuaternion(headQuat);
    if (hudRef.current) {
      hudRef.current.position
        .copy(headPos)
        .add(dir.copy(fwd).multiplyScalar(1.1))
        .add(right.clone().multiplyScalar(-0.5));
      hudRef.current.position.y = headPos.y - 0.45;
      hudRef.current.quaternion.copy(headQuat);
    }
  });

  if (!session) return null;

  return (
    <>
      {/* spatial gold subtitle */}
      <group ref={subRef}>
        {text && (
          <>
            <mesh position={[0, 0, -0.01]}>
              <planeGeometry args={[2.0, 0.5]} />
              <meshBasicMaterial color={0x0d0e11} transparent opacity={0.7} />
            </mesh>
            <Text maxWidth={1.85} fontSize={0.075} color="#e4be65" anchorX="center" anchorY="middle" textAlign="center">
              {text}
            </Text>
          </>
        )}
      </group>

      {/* head-anchored vitality + timer */}
      <group ref={hudRef}>
        <mesh>
          <planeGeometry args={[0.42, 0.24]} />
          <meshBasicMaterial color={0x0d0e11} transparent opacity={0.72} />
        </mesh>
        <Text position={[0, 0.06, 0.001]} fontSize={0.06} color="#f2c85b" anchorX="center" anchorY="middle">
          {hud.countdown}
        </Text>
        <Text position={[0, -0.04, 0.001]} fontSize={0.03} color="#62a8e5" anchorX="center" anchorY="middle">
          {`BREATH ${hud.breath}`}
        </Text>
        <Text position={[0, -0.09, 0.001]} fontSize={0.03} color="#4cd07a" anchorX="center" anchorY="middle">
          {`STAMINA ${hud.stamina}`}
        </Text>
      </group>
    </>
  );
}
